import React, { useEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Eraser } from 'lucide-react';

export function SignaturePad({ onChange, defaultName = '' }) {
  const sigRef = useRef(null);
  const [tab, setTab] = useState('draw');
  const [typed, setTyped] = useState(defaultName || '');

  // Generate signature image when typed name changes
  useEffect(() => {
    if (tab !== 'type') return;
    if (!typed.trim()) { onChange(null); return; }  // cleared the name: nothing to sign with
    {
      const canvas = document.createElement('canvas');
      canvas.width = 480; canvas.height = 120;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'rgba(255,255,255,0)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.font = 'italic 700 44px "Brush Script MT", "Lucida Handwriting", cursive';
      ctx.fillStyle = '#0f172a';
      ctx.fillText(typed, 16, 70);
      ctx.strokeStyle = '#0f172a';
      ctx.beginPath();
      ctx.moveTo(16, 92); ctx.lineTo(canvas.width - 16, 92); ctx.stroke();
      onChange(canvas.toDataURL('image/png'));
    }
  }, [typed, tab, onChange]);

  return (
    <div className="rounded-xl border bg-white p-3" data-testid="signature-pad">
      <Tabs value={tab} onValueChange={(v) => { setTab(v); if (v === 'draw' && sigRef.current) { sigRef.current.clear(); onChange(null); } }}>
        <TabsList>
          <TabsTrigger value="draw" data-testid="sig-tab-draw">Draw</TabsTrigger>
          <TabsTrigger value="type" data-testid="sig-tab-type">Type</TabsTrigger>
        </TabsList>
        <TabsContent value="draw" className="mt-3">
          <div className="rounded-lg border bg-white">
            <SignatureCanvas
              ref={sigRef}
              penColor="#0f172a"
              canvasProps={{ className: 'sig-pad-canvas w-full h-40 rounded-lg', 'data-testid': 'signature-canvas' }}
              onEnd={() => {
                if (!sigRef.current) return;
                // An untouched pad is not a signature: clearing it, or tapping without
                // drawing, must leave the Sign button disabled rather than send a blank PNG.
                onChange(sigRef.current.isEmpty() ? null : sigRef.current.getCanvas().toDataURL('image/png'));
              }}
            />
          </div>
          <div className="flex justify-between items-center mt-2 text-xs text-muted-foreground">
            <span>Signature is legally binding under ESIGN Act / UETA</span>
            <Button size="sm" variant="ghost" data-testid="signature-clear-button" onClick={() => { if (sigRef.current) { sigRef.current.clear(); onChange(null); } }}>
              <Eraser className="h-3.5 w-3.5 mr-1" /> Clear
            </Button>
          </div>
        </TabsContent>
        <TabsContent value="type" className="mt-3 space-y-2">
          <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Type full legal name" data-testid="signature-type-input" />
          <div className="rounded-lg border bg-muted p-3 text-2xl" style={{ fontFamily: '"Brush Script MT", "Lucida Handwriting", cursive', fontStyle: 'italic', borderBottom: '1px solid #0f172a', paddingBottom: 6 }}>
            {typed || 'Your typed signature preview'}
          </div>
          <div className="text-xs text-muted-foreground">By typing your name, you agree this constitutes your legal signature.</div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
